/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import SearchIcon from "@mui/icons-material/SearchOutlined";
import OpenIcon from "@mui/icons-material/KeyboardArrowDown";
import CloseIcon from "@mui/icons-material/KeyboardArrowUp";
import ChartIcon from "@mui/icons-material/ShowChartOutlined";
import ReplayIcon from "@mui/icons-material/Replay";
import ICompAnalysis from "../../../types/ICompAnalysis";
import Competitor from "./Competitor";
import { useNitroStore } from "../../../store/Store";
import { useNitroCAStore } from "../../../store/CompetitiveAnalysisStore";
import CompetitorOrigin from "./CompetitorOrigin";

const Competitors = ({
  records,
  reload,
  competitorOffer,
  setCompetitorOffer,
  activeFreightButton,
  setActiveFreightButton,
  product,
}: {
  records: ICompAnalysis[];
  reload: () => void;
  competitorOffer: number;
  setCompetitorOffer: (value: number) => void;
  activeFreightButton: any;
  setActiveFreightButton: (value: any) => void;
  product: string;
}) => {
  const [open, setOpen] = useState(false);
  const [selectedLocation, mode] = useNitroStore((state) => [
    state.selectedLocation,
    state.mode,
  ]);
  const [closeFreightDetailsPanel, setSelectYourOfferFreight, hiddenOrigins] =
    useNitroCAStore((state) => [
      state.closeRailFreightDetailsPanel,
      state.setSelectYourOfferFreight,
      state.hiddenOrigins,
    ]);

  const [selectedCompetitor, setSelectedCompetitor] = useState<any | null>(
    null,
  );
  const [competitorState, setCompetitorState] = useState<
    "new" | "history" | undefined
  >(undefined);
  const [newIndication, setNewIndication] = useState<{
    offer: number;
    freight: number;
    netback: number;
  } | null>(null);

  useEffect(() => {
    // Close indication history when new location is selected
    setSelectedCompetitor(false);
  }, [selectedLocation, setSelectedCompetitor]);

  useEffect(() => {
    // Reset competitor offer when location changes
    setCompetitorOffer(null as any);
  }, [product, setCompetitorOffer]);

  const [disabled, setDisabled] = useState(false);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    if (closeFreightDetailsPanel) {
      setNewIndication(null);
      setSelectedCompetitor(null);
    }
  }, [closeFreightDetailsPanel]);

  useEffect(() => {
    if (!records || records.length === 0) {
      setDisabled(true);
      setOpen(false);
    } else {
      setDisabled(false);
    }
  }, [records]);

  useEffect(() => {
    if (open) {
      // Scroll to bottom of competitor-chart
      const competitorChart = document.getElementById("competitor-chart");
      competitorChart?.scrollIntoView({ behavior: "smooth" });
    }
  }, [open]);

  const openIndicationHistory = (competitor: ICompAnalysis) => {
    setCompetitorState("history");
    setSelectedCompetitor(competitor);
  };

  const addIndication = (
    competitor: ICompAnalysis,
    indication: { offer: number; freight: number; netback: number },
  ) => {
    setNewIndication(indication);
    setCompetitorState("new");
    setSelectedCompetitor(competitor);
  };
  return (
    <>
      {selectedCompetitor && (
        <Competitor
          competitor={selectedCompetitor}
          defaultState={competitorState}
          reload={reload}
          close={() => {
            setSelectYourOfferFreight(true);
            setNewIndication(null);
            setSelectedCompetitor(null);
          }}
          newIndication={newIndication}
        />
      )}
      <div id="competitor-chart">
        <Paper
          style={{
            boxShadow: "none",
            border: "1px solid #d9d9d9",
            padding: open ? "6px 10px" : "6px 10px",
            paddingBottom: open ? 10 : 6,
            display: "flex",
            flexDirection: "column",
            gap: 0,
            borderRadius: open ? 10 : 15,
            backgroundColor: disabled ? "#eee" : "white",
          }}
        >
          {/* HEADER */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              cursor: disabled ? "default" : "pointer",
              opacity: disabled ? 0.4 : 1,
              height: 40,
            }}
            onClick={() => {
              if (disabled) return;
              setOpen(open === false);
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <ChartIcon />
              <div>Competitor Origins</div>
            </div>

            {open ? <CloseIcon /> : <OpenIcon />}
          </div>
          {/* END HEADER */}

          {/* BODY */}
          <div
            style={{
              display: open ? "flex" : "none",
              flexDirection: "column",
              gap: 10,
              paddingTop: 10,
            }}
          >
            {/* SEARCH BAR */}
            <div
              style={{
                padding: "0px 0px",
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                <TextField
                  fullWidth
                  InputProps={{
                    sx: {
                      padding: "0px 8px",
                      fontSize: 15,
                    },
                    startAdornment: (
                      <InputAdornment
                        position="start"
                        style={{
                          padding: 0,
                          margin: 0,
                          width: 15,
                        }}
                      >
                        $
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">DLVD</InputAdornment>
                    ),
                  }}
                  value={competitorOffer ?? ""}
                  onChange={(e) => {
                    let stringVal = String(e.target.value).replaceAll(",", "");
                    if (["-", "-0"].indexOf(stringVal) > -1) {
                      setCompetitorOffer(stringVal as any);
                      return;
                    }

                    const num = Number(stringVal);
                    if (Number.isNaN(num)) {
                      return;
                    }
                    if (num > 9999 || num < -9999) {
                      return;
                    }

                    if (stringVal.startsWith("0") && stringVal.length > 1) {
                      stringVal = stringVal.slice(1, stringVal.length);
                    }
                    const split = stringVal.split(".");
                    const val = Number(split[0]);
                    setCompetitorOffer(val);
                  }}
                />
                <Tooltip title="Reset to FOB price">
                  <IconButton
                    onClick={() => {
                      setCompetitorOffer(null as any);
                    }}
                    size="small"
                    style={{ border: "1px solid lightgray", borderRadius: 6 }}
                  >
                    <ReplayIcon style={{ fontSize: 20, color: "#005CA6" }} />
                  </IconButton>
                </Tooltip>
              </div>

              <TextField
                fullWidth
                InputProps={{
                  sx: { borderRadius: 16, padding: "0px 0px" },
                  startAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Search"
              />
            </div>
            {/* END SEARCH BAR */}

            <div>
              <div
                style={{
                  overflowX: "auto",
                  overflowY: "hidden",
                  maxWidth: 418,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                    paddingTop: 10,
                  }}
                >
                  {records.map((record) => {
                    const currentModeKey = mode === "TRUCK" ? "truck" : "rail";
                    const isHiddenByMode = hiddenOrigins[currentModeKey].has(
                      record.origin.name,
                    );

                    const isHiddenByFilter =
                      !!filter &&
                      !record.origin.name
                        .toLowerCase()
                        .includes(filter.toLowerCase());

                    return (
                      <CompetitorOrigin
                        key={record.id}
                        record={record}
                        competitorOffer={competitorOffer}
                        openIndicationHistory={openIndicationHistory}
                        addIndication={addIndication}
                        activeFreightButton={activeFreightButton}
                        setActiveFreightButton={setActiveFreightButton}
                        hidden={isHiddenByMode || isHiddenByFilter}
                      />
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* END BODY */}
        </Paper>
      </div>
    </>
  );
};

export default Competitors;
